import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CloudRain, Shield, Zap, Check, Upload, Link } from 'lucide-react';

const DEPLOY_STEPS = [
  { label: 'Uploading metadata to IPFS', icon: Upload, duration: 3000 },
  { label: 'Creating pool on Arbitrum', icon: Zap, duration: 8000 },
  { label: 'Confirming transaction', icon: Shield, duration: 5000 },
  { label: 'Fetching Rain link', icon: Link, duration: 4000 },
];

export const DeployingPopup = ({ deployPromise, onComplete, onError }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [deployDone, setDeployDone] = useState(false);
  const [deployResult, setDeployResult] = useState(undefined);

  useEffect(() => {
    const timers = [];
    let totalDelay = 0;

    DEPLOY_STEPS.forEach((step, index) => {
      timers.push(setTimeout(() => setActiveStep(index), totalDelay));
      totalDelay += step.duration;
      timers.push(setTimeout(() => setCompletedSteps((prev) => [...prev, index]), totalDelay));
    });

    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!deployPromise) {
      const fallbackTimer = setTimeout(() => onComplete(), 20500);
      return () => clearTimeout(fallbackTimer);
    }

    deployPromise
      .then((result) => {
        setDeployResult(result);
        setDeployDone(true);
      })
      .catch((err) => {
        if (onError) onError(err.message || 'Deployment failed');
      });
  }, [deployPromise, onComplete, onError]);

  useEffect(() => {
    if (deployDone && completedSteps.length === DEPLOY_STEPS.length) {
      const timer = setTimeout(() => onComplete(deployResult), 500);
      return () => clearTimeout(timer);
    }
  }, [deployDone, completedSteps.length, deployResult, onComplete]);

  const overallProgress = (completedSteps.length / DEPLOY_STEPS.length) * 100;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="gm-deploy-overlay"
    >
      <div className="gm-deploy-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="gm-deploy-card"
        >
          <div className="gm-deploy-icon-wrap">
            <motion.div
              className="gm-deploy-icon-box"
              animate={{
                boxShadow: [
                  '0 0 0px hsl(var(--primary) / 0)',
                  '0 0 30px hsl(var(--primary) / 0.3)',
                  '0 0 0px hsl(var(--primary) / 0)',
                ],
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <CloudRain className="gm-deploy-main-icon" />
              </motion.div>
            </motion.div>
          </div>

          <motion.h2
            className="gm-deploy-title"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Deploying to Rain
          </motion.h2>
          <motion.p
            className="gm-deploy-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Creating your market on Arbitrum...
          </motion.p>

          <div className="gm-deploy-progress-wrap">
            <div className="gm-deploy-progress-track">
              <motion.div
                className="gm-deploy-progress-fill"
                initial={{ width: '0%' }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              />
            </div>
          </div>

          <div className="gm-deploy-steps">
            {DEPLOY_STEPS.map((step, index) => {
              const isCompleted = completedSteps.includes(index);
              const isActive = activeStep === index && !isCompleted;
              const Icon = step.icon;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index }}
                  className={`gm-deploy-step ${
                    isActive
                      ? 'gm-deploy-step--active'
                      : isCompleted
                        ? 'gm-deploy-step--done'
                        : 'gm-deploy-step--pending'
                  }`}
                >
                  <div
                    className={`gm-deploy-step-icon ${
                      isCompleted
                        ? 'gm-deploy-step-icon--done'
                        : isActive
                          ? 'gm-deploy-step-icon--active'
                          : 'gm-deploy-step-icon--pending'
                    }`}
                  >
                    {isCompleted ? (
                      <Check className="gm-icon-sm" />
                    ) : isActive ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="gm-deploy-spinner"
                      />
                    ) : (
                      <Icon className="gm-icon-sm" />
                    )}
                  </div>
                  <span
                    className={`gm-deploy-step-label ${
                      isActive ? 'gm-deploy-step-label--active' : ''
                    }`}
                  >
                    {step.label}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};
